/**
 * Screen Context Summary
 *
 * Builds the human-readable "what's on screen" text from
 * active-page-context-store + screen-context-registry + the current text
 * selection. AssistantOverlayHost's `beforeRouterRequest` hook prepends it
 * to the user's message every turn — that is the only way it reaches the
 * model. (The adapter's own `contextProvider` path doesn't carry it:
 * @neuro/chat-adapter's context-change system message only knows
 * `repoId`/`noteId`/`planId` and emits a content-free "Context set:
 * Screen." for anything else.)
 *
 * Content source, in priority order:
 *   1. A registered screen-context-registry contributor for the current
 *      page — curated, semantic (e.g. "note titled X, id Y"). Four core
 *      pages have one (search, agent cockpit, workflows, graph); a plugin
 *      can add one through plugin-screen-context-bridge.ts. Only core
 *      contributors and the current page's own plugin count.
 *   2. Otherwise, a generic capture of the page's own visible text via
 *      `.innerText` on `.page-action-layout` — the wrapper every page
 *      (core or plugin) renders its content inside, whether that's
 *      `CorePageWrapper` or `PluginPageHost`'s `page.component` path (see
 *      PageActionLayout.tsx). This needs *no* plugin changes: plugin pages
 *      render as plain React components in the same DOM as the host.
 *      Using the *live* element's `.innerText` (not a detached clone)
 *      means it naturally reflects real visibility — a closed
 *      CommandPalette or a mobile-hidden PageToolbar contribute nothing,
 *      no chrome-selector denylist needed. Capped like the selection
 *      capture below to avoid reintroducing the token-bloat problem
 *      fixed in curated-backend-tools.ts.
 */
import { useActivePageContextStore } from '../../plugins/registries/active-page-context-store';
import { useScreenContextRegistry } from '../../plugins/registries/screen-context-registry';
import { getEntityLabel } from '../../plugins/registries/entity-type-registry';

const PAGE_CONTENT_SELECTOR = '.page-action-layout';

export interface ScreenContextPayload {
  summary: string;
  data: {
    pageId: string;
    entityType?: string;
    entityId?: string;
    title?: string;
    contributions: unknown[];
    visibleText?: string;
    selection?: string;
  };
}

function captureSelectionText(maxLen = 2000): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const selection = window.getSelection?.()?.toString().trim();
  if (!selection) return undefined;
  return selection.length > maxLen ? `${selection.slice(0, maxLen)}…` : selection;
}

/** Generic fallback for pages with no registered contributor — see the
 * module doc for why `.page-action-layout` + live `.innerText` (not a
 * detached clone) needs no plugin changes and no chrome denylist. */
function captureVisiblePageText(maxLen = 3000): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const root = document.querySelector<HTMLElement>(PAGE_CONTENT_SELECTOR);
  if (!root) return undefined;
  const text = (root.innerText || '').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
  if (!text) return undefined;
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

const PAGE_LABEL_MAX = 60;

/**
 * A short name for the page the user is on: its own main heading, else its
 * route. (No page sets `document.title`, so that's always just the app name.)
 */
export function currentPageLabel(): string | undefined {
  const pageId = useActivePageContextStore.getState().context?.pageId;
  if (typeof document !== 'undefined') {
    const heading = document
      .querySelector<HTMLElement>(`${PAGE_CONTENT_SELECTOR} h1, ${PAGE_CONTENT_SELECTOR} h2`)
      ?.innerText?.replace(/\s+/g, ' ')
      .trim();
    if (heading) return heading.length > PAGE_LABEL_MAX ? `${heading.slice(0, PAGE_LABEL_MAX)}…` : heading;
  }
  return pageId || undefined;
}

/** Returns null if there's no active page context to report. */
export function buildScreenContextPayload(): ScreenContextPayload | null {
  const pageContext = useActivePageContextStore.getState().context;
  if (!pageContext) return null;

  // A plugin's contribution only counts on that plugin's own pages, so a
  // snapshot it published elsewhere can't stick to every page (or hide the
  // visible-text capture there).
  const contributions = useScreenContextRegistry
    .getState()
    .getContextForPage(pageContext.pageId, ['core', ...(pageContext.activePluginId ? [pageContext.activePluginId] : [])]);

  const summaryLines: string[] = [`Route: ${pageContext.pageId}`];
  if (pageContext.entityType) {
    summaryLines.push(
      `Viewing a ${getEntityLabel(pageContext.entityType)}` +
        (pageContext.entityId ? ` (id: ${pageContext.entityId})` : ''),
    );
  }
  let visibleText: string | undefined;
  if (contributions.length > 0) {
    for (const c of contributions) summaryLines.push(c.summary);
  } else {
    visibleText = captureVisiblePageText();
    if (visibleText) summaryLines.push(`Visible page content:\n${visibleText}`);
  }
  const selection = captureSelectionText();
  if (selection) summaryLines.push(`Selected text: "${selection}"`);

  return {
    summary: summaryLines.join('\n'),
    data: {
      pageId: pageContext.pageId,
      entityType: pageContext.entityType,
      entityId: pageContext.entityId,
      title: typeof document !== 'undefined' ? document.title : undefined,
      contributions: contributions.map((c) => c.data).filter(Boolean),
      visibleText,
      selection,
    },
  };
}
